import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export type STDParameters = {
  eliminationPeriod: string;
  benefitDuration: string;
  benefitPercentage: string;
  maximumWeeklyBenefit: string;
  customMaxWeeklyBenefit?: number;
  minimumWeeklyBenefit: string;
};

export type LTDParameters = {
  eliminationPeriod: string;
  benefitDuration: string;
  benefitPercentage: string;
  maximumMonthlyBenefit: string;
  customMaxMonthlyBenefit?: number;
  definitionOfDisability: string;
  coveredEarningsDefinition: string;
};

export type DisabilityRFPParameters = {
  coverageTypes: string[];
  stdParams?: STDParameters;
  ltdParams?: LTDParameters;
};

type DisabilityRFPFormProps = {
  value: DisabilityRFPParameters;
  onChange: (value: DisabilityRFPParameters) => void;
};

export function DisabilityRFPForm({ value, onChange }: DisabilityRFPFormProps) {
  const updateField = (field: keyof DisabilityRFPParameters, newValue: any) => {
    onChange({ ...value, [field]: newValue });
  };

  const toggleCoverageType = (type: string) => {
    const current = value.coverageTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    updateField('coverageTypes', updated);
  };

  const updateSTDParam = (field: keyof STDParameters, newValue: any) => {
    const currentStd = value.stdParams || {};
    updateField('stdParams', { ...currentStd, [field]: newValue });
  };

  const updateLTDParam = (field: keyof LTDParameters, newValue: any) => {
    const currentLtd = value.ltdParams || {};
    updateField('ltdParams', { ...currentLtd, [field]: newValue });
  };

  const hasSTD = value.coverageTypes?.includes('short_term');
  const hasLTD = value.coverageTypes?.includes('long_term');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CoverageIcon planType="ltd" size="sm" />
          Disability
        </CardTitle>
        <p className="text-xs text-muted-foreground">When payments start, how long they last, and how much.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Disability Type */}
        <div className="space-y-3">
          <Label>Disability Type</Label>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="disability-short"
                checked={hasSTD}
                onCheckedChange={() => toggleCoverageType('short_term')}
              />
              <label htmlFor="disability-short" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Short-Term Disability (STD)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="disability-long"
                checked={hasLTD}
                onCheckedChange={() => toggleCoverageType('long_term')}
              />
              <label htmlFor="disability-long" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Long-Term Disability (LTD)
              </label>
            </div>
          </div>
        </div>

        {/* STD Parameters */}
        {hasSTD && (
          <div className="border rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-sm">Short-Term Disability (STD) Parameters</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="std-elimination">Elimination Period</Label>
                <Select 
                  value={value.stdParams?.eliminationPeriod || ''} 
                  onValueChange={(v) => updateSTDParam('eliminationPeriod', v)}
                >
                  <SelectTrigger id="std-elimination">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0_days">0 Days</SelectItem>
                    <SelectItem value="7_days">7 Days</SelectItem>
                    <SelectItem value="14_days">14 Days</SelectItem>
                    <SelectItem value="30_days">30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="std-duration">Benefit Duration</Label>
                <Select 
                  value={value.stdParams?.benefitDuration || ''} 
                  onValueChange={(v) => updateSTDParam('benefitDuration', v)}
                >
                  <SelectTrigger id="std-duration">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="9_weeks">9 Weeks</SelectItem>
                    <SelectItem value="13_weeks">13 Weeks</SelectItem>
                    <SelectItem value="15_weeks">15 Weeks</SelectItem>
                    <SelectItem value="17_weeks">17 Weeks</SelectItem>
                    <SelectItem value="26_weeks">26 Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="std-percentage">Benefit Percentage</Label>
                <Select 
                  value={value.stdParams?.benefitPercentage || ''} 
                  onValueChange={(v) => updateSTDParam('benefitPercentage', v)}
                >
                  <SelectTrigger id="std-percentage">
                    <SelectValue placeholder="Select percentage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="60">60%</SelectItem>
                    <SelectItem value="66.67">66 2/3%</SelectItem>
                    <SelectItem value="70">70%</SelectItem>
                    <SelectItem value="75">75%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="std-max-weekly">Maximum Weekly Benefit</Label>
                <Select 
                  value={value.stdParams?.maximumWeeklyBenefit || ''} 
                  onValueChange={(v) => updateSTDParam('maximumWeeklyBenefit', v)}
                >
                  <SelectTrigger id="std-max-weekly">
                    <SelectValue placeholder="Select maximum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="500">$500</SelectItem>
                    <SelectItem value="750">$750</SelectItem>
                    <SelectItem value="1000">$1,000</SelectItem>
                    <SelectItem value="1500">$1,500</SelectItem>
                    <SelectItem value="2000">$2,000</SelectItem>
                    <SelectItem value="2500">$2,500</SelectItem>
                    <SelectItem value="3000">$3,000</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {value.stdParams?.maximumWeeklyBenefit === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="std-custom-max">Custom Maximum Weekly Benefit</Label>
                  <Input
                    id="std-custom-max"
                    type="number"
                    placeholder="Enter amount"
                    value={value.stdParams?.customMaxWeeklyBenefit || ''}
                    onChange={(e) => updateSTDParam('customMaxWeeklyBenefit', parseInt(e.target.value) || undefined)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="std-min-weekly">Minimum Weekly Benefit</Label>
                <Select 
                  value={value.stdParams?.minimumWeeklyBenefit || ''} 
                  onValueChange={(v) => updateSTDParam('minimumWeeklyBenefit', v)}
                >
                  <SelectTrigger id="std-min-weekly">
                    <SelectValue placeholder="Select minimum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="25">$25</SelectItem>
                    <SelectItem value="50">$50</SelectItem>
                    <SelectItem value="100">$100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* LTD Parameters */}
        {hasLTD && (
          <div className="border rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-sm">Long-Term Disability (LTD) Parameters</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ltd-elimination">Elimination Period</Label>
                <Select 
                  value={value.ltdParams?.eliminationPeriod || ''} 
                  onValueChange={(v) => updateLTDParam('eliminationPeriod', v)}
                >
                  <SelectTrigger id="ltd-elimination">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90_days">90 Days</SelectItem>
                    <SelectItem value="180_days">180 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ltd-duration">Benefit Duration</Label>
                <Select 
                  value={value.ltdParams?.benefitDuration || ''} 
                  onValueChange={(v) => updateLTDParam('benefitDuration', v)}
                >
                  <SelectTrigger id="ltd-duration">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2_years">2 Years</SelectItem>
                    <SelectItem value="5_years">5 Years</SelectItem>
                    <SelectItem value="age_65">Age 65</SelectItem>
                    <SelectItem value="age_67">Age 67</SelectItem>
                    <SelectItem value="ssnra">SSNRA</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ltd-percentage">Benefit Percentage</Label>
                <Select 
                  value={value.ltdParams?.benefitPercentage || ''} 
                  onValueChange={(v) => updateLTDParam('benefitPercentage', v)}
                >
                  <SelectTrigger id="ltd-percentage">
                    <SelectValue placeholder="Select percentage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="60">60%</SelectItem>
                    <SelectItem value="66.67">66 2/3%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ltd-max-monthly">Maximum Monthly Benefit</Label>
                <Select 
                  value={value.ltdParams?.maximumMonthlyBenefit || ''} 
                  onValueChange={(v) => updateLTDParam('maximumMonthlyBenefit', v)}
                >
                  <SelectTrigger id="ltd-max-monthly">
                    <SelectValue placeholder="Select maximum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3000">$3,000</SelectItem>
                    <SelectItem value="5000">$5,000</SelectItem>
                    <SelectItem value="7500">$7,500</SelectItem>
                    <SelectItem value="10000">$10,000</SelectItem>
                    <SelectItem value="15000">$15,000</SelectItem>
                    <SelectItem value="20000">$20,000</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {value.ltdParams?.maximumMonthlyBenefit === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="ltd-custom-max">Custom Maximum Monthly Benefit</Label>
                  <Input
                    id="ltd-custom-max"
                    type="number"
                    placeholder="Enter amount"
                    value={value.ltdParams?.customMaxMonthlyBenefit || ''}
                    onChange={(e) => updateLTDParam('customMaxMonthlyBenefit', parseInt(e.target.value) || undefined)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="ltd-definition">Definition of Disability</Label>
                <Select 
                  value={value.ltdParams?.definitionOfDisability || ''} 
                  onValueChange={(v) => updateLTDParam('definitionOfDisability', v)}
                >
                  <SelectTrigger id="ltd-definition">
                    <SelectValue placeholder="Select definition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="own_occ_12">Own-Occupation - 12 Months</SelectItem>
                    <SelectItem value="own_occ_24">Own-Occupation - 24 Months</SelectItem>
                    <SelectItem value="any_occ">Any Occupation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ltd-earnings">Covered Earnings Definition</Label>
                <Select 
                  value={value.ltdParams?.coveredEarningsDefinition || ''} 
                  onValueChange={(v) => updateLTDParam('coveredEarningsDefinition', v)}
                >
                  <SelectTrigger id="ltd-earnings">
                    <SelectValue placeholder="Select definition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="base_only">Base Only</SelectItem>
                    <SelectItem value="base_plus_commission">Base Plus Commission</SelectItem>
                    <SelectItem value="w2_earnings">W2 Earnings</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
